import React, { useState, useEffect } from 'react';
import { FaShieldAlt, FaHeartbeat, FaPiggyBank, FaSpinner, FaBrain } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InsuranceAnalysis = ({ clientId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);

  const [formData, setFormData] = useState({
    annual_income: '',
    self_maintenance_percent: 30,
    years_coverage: 20,
    expected_return_percent: 8,
    inflation_percent: 6,
    outstanding_debts: 0,
    existing_savings: 0,
    existing_life_cover: 0,
    current_health_cover: 0,
    monthly_expense: '',
    months_required: 6,
    family_size: 4
  });

  useEffect(() => {
    fetchAnalysis();
  }, [clientId]);

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/insurance-analysis/${clientId}`);
      setAnalysis(response.data);
      setFormData({
        annual_income: response.data.annual_income,
        self_maintenance_percent: response.data.self_maintenance_percent,
        years_coverage: response.data.years_coverage,
        expected_return_percent: response.data.expected_return_percent,
        inflation_percent: response.data.inflation_percent,
        outstanding_debts: response.data.outstanding_debts,
        existing_savings: response.data.existing_savings,
        existing_life_cover: response.data.existing_life_cover,
        current_health_cover: response.data.current_health_cover,
        monthly_expense: response.data.monthly_expense,
        months_required: response.data.months_required,
        family_size: response.data.family_size
      });
    } catch (error) {
      if (error.response?.status !== 404) {
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: parseFloat(value) || value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSend = {
        ...formData,
        client_id: clientId,
        existing_emergency_savings: formData.existing_savings
      };

      const response = await axios.post(`${API}/insurance-analysis`, dataToSend);
      setAnalysis(response.data);
      fetchAIInsights();
    } catch (error) {
      alert('Failed to calculate insurance analysis');
    } finally {
      setSaving(false);
    }
  };

  const fetchAIInsights = async () => {
    setLoadingAI(true);
    try {
      const response = await axios.get(`${API}/insurance-analysis/${clientId}/ai-insights`);
      setAiInsights(response.data);
    } catch (error) {
    } finally {
      setLoadingAI(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    const lakhs = value / 100000;
    return `₹${lakhs.toFixed(2)}L`;
  };

  const lifeChartData = analysis ? [
    { name: 'Existing Cover', value: analysis.existing_life_cover / 100000, fill: '#1E3A5F' },
    { name: 'Gap', value: analysis.protection_gap / 100000, fill: '#FF6B6B' },
  ] : [];

  const healthChartData = analysis ? [
    { name: 'Covered', value: analysis.current_health_cover / 100000, fill: '#4CAF50' },
    { name: 'Gap', value: analysis.health_gap / 100000, fill: '#FF9800' },
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-[#FFD700]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h2 className="text-2xl font-bold text-[#FFD700] mb-2">🛡️ Income & Debt Protection</h2>
        <p className="text-gray-300">Life Insurance Gap Analysis (Human Life Value Method)</p>
      </div>

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Life Insurance Inputs */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-xl font-bold text-[#FFD700] mb-4">📊 Life Insurance Calculation</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Annual Income (₹)</label>
              <input
                type="number"
                name="annual_income"
                value={formData.annual_income}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20"
                placeholder="1200000"
                required
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Self Maintenance (%)</label>
              <input
                type="number"
                name="self_maintenance_percent"
                value={formData.self_maintenance_percent}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Years Coverage</label>
              <input
                type="number"
                name="years_coverage"
                value={formData.years_coverage}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Expected Return (%)</label>
              <input
                type="number"
                step="0.1"
                name="expected_return_percent"
                value={formData.expected_return_percent}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Inflation (%)</label>
              <input
                type="number"
                step="0.1"
                name="inflation_percent"
                value={formData.inflation_percent}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Outstanding Debts (₹)</label>
              <input
                type="number"
                name="outstanding_debts"
                value={formData.outstanding_debts}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Existing Savings (₹)</label>
              <input
                type="number"
                name="existing_savings"
                value={formData.existing_savings}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Existing Life Cover (₹)</label>
              <input
                type="number"
                name="existing_life_cover"
                value={formData.existing_life_cover}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20"
              />
            </div>
          </div>
        </div>

        {/* Health & Emergency */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-xl font-bold text-[#FFD700] mb-4">🏥 Health & Emergency Planning</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Current Health Cover (₹)</label>
              <input
                type="number"
                name="current_health_cover"
                value={formData.current_health_cover}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Monthly Expense (₹)</label>
              <input
                type="number"
                name="monthly_expense"
                value={formData.monthly_expense}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20"
                required
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Emergency Fund (Months)</label>
              <input
                type="number"
                name="months_required"
                value={formData.months_required}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20"
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all transform hover:scale-[1.02] disabled:opacity-50 flex items-center justify-center"
        >
          {saving ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              Calculating...
            </>
          ) : (
            'Calculate Insurance Analysis'
          )}
        </button>
      </form>

      {/* Results */}
      {analysis && (
        <div className="space-y-6">
          {/* Life Insurance Gap */}
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
            <h3 className="text-xl font-bold text-[#FFD700] mb-4">🛡️ Life Insurance Gap Analysis</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-[#0A1929] rounded-lg p-4 border border-[#2D5A8F]">
                <p className="text-gray-400 text-sm mb-2">HLV Value</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(analysis.hlv_value)}</p>
              </div>

              <div className="bg-[#0A1929] rounded-lg p-4 border border-[#2D5A8F]">
                <p className="text-gray-400 text-sm mb-2">Required Cover</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(analysis.required_cover)}</p>
              </div>

              <div className="bg-[#0A1929] rounded-lg p-4 border border-[#D4AF37]">
                <p className="text-gray-400 text-sm mb-2">Protection Gap</p>
                <p className="text-2xl font-bold text-[#FFD700]">{formatCurrency(analysis.protection_gap)}</p>
                {analysis.protection_gap > 0 && (
                  <p className="text-red-400 text-xs mt-1">⚠️ Critical Gap: Action Required</p>
                )}
              </div>
            </div>

            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={lifeChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D5A8F" />
                <XAxis dataKey="name" stroke="#B0BEC5" />
                <YAxis stroke="#B0BEC5" label={{ value: 'Amount (Lakhs)', angle: -90, position: 'insideLeft', fill: '#B0BEC5' }} />
                <Tooltip contentStyle={{ backgroundColor: '#1A2942', border: '1px solid #2D5A8F' }} />
                <Legend />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Health Insurance */}
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
            <h3 className="text-xl font-bold text-[#FFD700] mb-4">🏥 Health Insurance Gap</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-[#0A1929] rounded-lg p-4 border border-[#2D5A8F]">
                <p className="text-gray-400 text-sm mb-2">Required Health Cover</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(analysis.required_health_cover)}</p>
              </div>

              <div className="bg-[#0A1929] rounded-lg p-4 border border-[#D4AF37]">
                <p className="text-gray-400 text-sm mb-2">Health Gap</p>
                <p className="text-2xl font-bold text-[#FFD700]">{formatCurrency(analysis.health_gap)}</p>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={healthChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D5A8F" />
                <XAxis dataKey="name" stroke="#B0BEC5" />
                <YAxis stroke="#B0BEC5" />
                <Tooltip contentStyle={{ backgroundColor: '#1A2942', border: '1px solid #2D5A8F' }} />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Emergency Fund */}
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
            <h3 className="text-xl font-bold text-[#FFD700] mb-4">💰 Emergency Fund</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#0A1929] rounded-lg p-4 border border-[#2D5A8F]">
                <p className="text-gray-400 text-sm mb-2">Required Emergency Fund</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(analysis.emergency_fund_required)}</p>
                <p className="text-gray-400 text-xs mt-2">{formData.months_required} months of expenses</p>
              </div>

              <div className="bg-[#0A1929] rounded-lg p-4 border border-[#D4AF37]">
                <p className="text-gray-400 text-sm mb-2">Emergency Fund Gap</p>
                <p className="text-2xl font-bold text-[#FFD700]">{formatCurrency(analysis.emergency_fund_gap)}</p>
              </div>
            </div>
          </div>

          {/* AI Insights */}
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#D4AF37]">
            <div className="flex items-center space-x-3 mb-4">
              <FaBrain className="text-[#FFD700]" size={24} />
              <h3 className="text-xl font-bold text-[#FFD700]">✨ AI-Powered Insurance Recommendations</h3>
            </div>

            {loadingAI ? (
              <div className="flex items-center justify-center py-8">
                <FaSpinner className="animate-spin text-[#FFD700]" size={32} />
              </div>
            ) : aiInsights ? (
              <div className="space-y-4">
                <div>
                  <h4 className="text-white font-semibold mb-2">Priority Assessment</h4>
                  <p className="text-gray-300">{aiInsights.priority_assessment}</p>
                </div>

                <div>
                  <h4 className="text-white font-semibold mb-2">Life Insurance Products</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-300">
                    {aiInsights.life_insurance_products?.map((product, idx) => (
                      <li key={idx}>{product}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-white font-semibold mb-2">Health Insurance Products</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-300">
                    {aiInsights.health_insurance_products?.map((product, idx) => (
                      <li key={idx}>{product}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-white font-semibold mb-2">Emergency Fund Strategy</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-300">
                    {aiInsights.emergency_fund_strategy?.map((strategy, idx) => (
                      <li key={idx}>{strategy}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <button
                onClick={fetchAIInsights}
                className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all"
              >
                Get AI Insurance Recommendations
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InsuranceAnalysis;